/**
 * Alshallal Pastry Company - Document Preview Component
 * Universal document preview with view, print, and download capabilities
 * 
 * Supports:
 * - PDFs: Inline viewer with zoom
 * - Images: Direct display with zoom
 * - Other files: Download fallback
 * 
 * Features:
 * - Full-screen preview
 * - Print functionality
 * - Download button
 * - Zoom controls for images
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Download,
  Printer,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  Minimize2,
  FileText,
  Image as ImageIcon,
  File,
  Loader2,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// File type detection
const getFileType = (fileName, mimeType) => {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  
  // Check by extension
  if (['pdf'].includes(extension)) return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension)) return 'image';
  
  // Check by MIME type
  if (mimeType?.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  
  return 'other';
};

const DocumentPreview = ({
  open,
  onClose,
  document: docData,
  downloadUrl,
  token,
  isRTL = false,
  // Optional: for company documents vs employee documents
  documentType = 'employee', // 'employee' | 'company' | 'letter'
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const iframeRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const fileType = docData ? getFileType(docData.file_name, docData.mime_type) : 'other';
  const fileName = docData?.display_name || docData?.file_name || 'Document';

  // Fetch document blob for preview
  // Phase E1.3: Uses signed URLs for cloud storage (GCS)
  //
  // BUG-FIX (2026-02-16): Several callers pass a new `document` object literal
  // on every parent render (e.g. HRLettersPage constructs `{id, file_name,
  // mime_type}` inline). Using the whole `docData` object in this effect's
  // dep list caused the effect to re-run on every parent render, revoking the
  // freshly-created blob URL and aborting the iframe fetch (net::ERR_ABORTED).
  // We now key the effect on the STABLE `docData?.id` + `downloadUrl` string,
  // and track the current blob via a ref so overlapping fetches from React
  // StrictMode / rapid re-renders can't leak or revoke each other's URLs.
  const docId = docData?.id;
  const currentBlobRef = useRef(null);
  useEffect(() => {
    if (!open || !docId) return;

    let cancelled = false;

    const fetchDocument = async () => {
      setLoading(true);
      setError(null);
      setBlobUrl(null);

      // Get base URL without /api suffix for constructing full URLs
      const BASE_URL = process.env.REACT_APP_BACKEND_URL;

      try {
        // Phase E1.3: Try to get a signed URL first for cloud storage
        let signedUrl = null;
        let fallbackUrl = null;

        if (documentType === 'company') {
          // Try signed URL endpoint
          const signedUrlResponse = await fetch(
            `${API_URL}/storage/company-documents/${docId}/preview-url?inline=true`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (signedUrlResponse.ok) {
            const signedData = await signedUrlResponse.json();
            // Any cloud provider (gcs, s3, azure) returns a self-authenticating
            // pre-signed URL. Only "legacy" means we must hit the API endpoint.
            if (signedData.type && signedData.type !== 'legacy' && signedData.type !== 'local') {
              signedUrl = signedData.url;
            } else {
              // Legacy / local path — goes through API with auth
              const path = signedData.url || '';
              fallbackUrl = path.startsWith('http') ? path : `${BASE_URL}${path}`;
            }
          }
          fallbackUrl = fallbackUrl || `${API_URL}/company-documents/${docId}/download`;
        } else if (documentType === 'letter') {
          fallbackUrl = `${API_URL}/letters/my/documents/${docId}/download`;
        } else if (documentType === 'qc' || documentType === 'direct') {
          // QC violation evidence is streamed only through the authenticated HR
          // endpoint passed in `downloadUrl`. There is no signed-URL probe, and
          // deliberately no public link.
          fallbackUrl = null;
        } else {
          // Employee document - try signed URL
          const signedUrlResponse = await fetch(
            `${API_URL}/storage/documents/${docId}/preview-url?inline=true`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (signedUrlResponse.ok) {
            const signedData = await signedUrlResponse.json();
            if (signedData.type && signedData.type !== 'legacy' && signedData.type !== 'local') {
              signedUrl = signedData.url;
            } else {
              const path = signedData.url || '';
              fallbackUrl = path.startsWith('http') ? path : `${BASE_URL}${path}`;
            }
          }
          fallbackUrl = fallbackUrl || `${API_URL}/documents/${docId}/download`;
        }

        // Always fetch the bytes through the AUTHENTICATED backend endpoint
        // (the server streams from storage). This avoids any browser-direct
        // cross-origin fetch to cloud storage, so there is NO R2/S3 CORS
        // dependency and view + download behave identically for local and
        // cloud providers. `signedUrl` is still probed above so the backend can
        // audit the view and flag genuinely-missing files.
        const url = downloadUrl || fallbackUrl;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch document');
        }
        const blob = await response.blob();

        if (cancelled) return; // Effect was superseded — drop this result

        const objectUrl = URL.createObjectURL(blob);
        // Revoke any previous blob we were tracking BEFORE replacing it, so
        // rapid re-mounts (StrictMode double-invoke) never leak URLs.
        if (currentBlobRef.current) {
          URL.revokeObjectURL(currentBlobRef.current);
        }
        currentBlobRef.current = objectUrl;
        setBlobUrl(objectUrl);
      } catch (err) {
        if (cancelled) return;
        console.error('Document preview error:', err);
        setError(isRTL ? 'فشل في تحميل المستند' : 'Failed to load document');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDocument();

    return () => {
      cancelled = true;
    };
  }, [open, docId, downloadUrl, token, documentType, isRTL]);

  // Revoke the tracked blob URL only when the component fully unmounts.
  useEffect(() => {
    return () => {
      if (currentBlobRef.current) {
        URL.revokeObjectURL(currentBlobRef.current);
        currentBlobRef.current = null;
      }
    };
  }, []);

  // Handle print
  //
  // A2 (2026-08-25): this used to build the print document with
  // `printWindow.document.write(...)`, interpolating `fileName` straight into
  // `<title>` and `alt=`. `fileName` is the uploader-supplied display name /
  // file name and is never HTML-escaped server side, so a name like
  // `</title><script>...</script>` executed in the approver's own origin and
  // could read their token out of localStorage. The document is now assembled
  // with DOM APIs: assigning `.title`, `.alt` and `.textContent` treats the
  // value as inert text, and there is no inline <script> any more.
  const handlePrint = () => {
    if (fileType === 'pdf' && iframeRef.current) {
      iframeRef.current.contentWindow?.print();
      return;
    }

    if (fileType !== 'image' || !blobUrl) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printDoc = printWindow.document;

    const style = printDoc.createElement('style');
    style.textContent = `
      body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
      img { max-width: 100%; max-height: 100vh; object-fit: contain; }
      @media print {
        body { margin: 0; }
        img { max-width: 100%; max-height: 100%; }
      }
    `;
    printDoc.head.appendChild(style);
    printDoc.title = fileName;

    const img = printDoc.createElement('img');
    img.alt = fileName;
    img.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
    img.src = blobUrl;
    printDoc.body.appendChild(img);
  };

  // Handle download
  const handleDownload = () => {
    if (blobUrl) {
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = docData?.file_name || 'document';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    }
  };

  // Zoom controls
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleResetZoom = () => {
    setZoom(100);
    setRotation(0);
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      window.document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!window.document.fullscreenElement);
    };
    window.document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => window.document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-500">
          <Loader2 className="w-12 h-12 animate-spin mb-4" />
          <p>{isRTL ? 'جاري تحميل المستند...' : 'Loading document...'}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-500">
          <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <p className="text-red-600">{error}</p>
          <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
            {isRTL ? 'إعادة المحاولة' : 'Retry'}
          </Button>
        </div>
      );
    }

    if (!blobUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-500">
          <File className="w-12 h-12 mb-4 opacity-50" />
          <p>{isRTL ? 'لا يوجد ملف للعرض' : 'No file to display'}</p>
        </div>
      );
    }

    // PDF Preview
    //
    // The critical bug this component previously had was in the useEffect
    // above — callers pass an inline object literal for `docData` on every
    // render, which caused the effect to re-run and revoke the fresh blob URL
    // mid-load, aborting the iframe fetch (net::ERR_ABORTED). That is now
    // fixed by keying the effect on the stable `docData?.id`. The backend
    // also serves the PDF with `Content-Disposition: inline` so the browser's
    // native PDF viewer renders it inside the iframe.
    if (fileType === 'pdf') {
      return (
        <iframe
          ref={iframeRef}
          src={`${blobUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
          className="w-full h-full border-0"
          title={fileName}
          style={{ minHeight: '70vh' }}
          data-testid="document-preview-pdf"
        />
      );
    }

    // Image Preview
    if (fileType === 'image') {
      return (
        <div 
          className="flex items-center justify-center h-full overflow-auto p-4"
          style={{ backgroundColor: '#1a1a1a' }}
        >
          <img
            ref={imageRef}
            src={blobUrl}
            alt={fileName}
            className="max-w-full transition-transform duration-200"
            style={{
              transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
              maxHeight: zoom <= 100 ? '100%' : 'none',
            }}
          />
        </div>
      );
    }

    // Other file types - show download prompt
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <File className="w-16 h-16 mb-4 opacity-50" />
        <p className="text-lg font-medium mb-2">{fileName}</p>
        <p className="text-sm mb-4">
          {isRTL 
            ? 'هذا النوع من الملفات لا يمكن عرضه. يمكنك تحميله.'
            : 'This file type cannot be previewed. You can download it.'}
        </p>
        <Button onClick={handleDownload}>
          <Download className="w-4 h-4 mr-2" />
          {isRTL ? 'تحميل الملف' : 'Download File'}
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        ref={containerRef}
        className={cn(
          "max-w-6xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col",
          isFullscreen && "max-w-none w-screen h-screen rounded-none"
        )}
        aria-describedby={undefined}
      >
        {/* A11y: visually-hidden title for screen readers (Radix requires it). */}
        <VisuallyHidden.Root asChild>
          <DialogTitle>{fileName}</DialogTitle>
        </VisuallyHidden.Root>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            {fileType === 'pdf' && <FileText className="w-5 h-5 text-red-500" />}
            {fileType === 'image' && <ImageIcon className="w-5 h-5 text-blue-500" />}
            {fileType === 'other' && <File className="w-5 h-5 text-gray-500" />}
            <div>
              <h3 className="font-medium text-sm">{fileName}</h3>
              {docData?.document_type_name && (
                <p className="text-xs text-gray-500">{docData.document_type_name}</p>
              )}
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-1">
            {/* Zoom controls (for images) */}
            {fileType === 'image' && blobUrl && (
              <>
                <Button variant="ghost" size="sm" onClick={handleZoomOut} disabled={zoom <= 25}>
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-xs w-12 text-center">{zoom}%</span>
                <Button variant="ghost" size="sm" onClick={handleZoomIn} disabled={zoom >= 200}>
                  <ZoomIn className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleRotate}>
                  <RotateCw className="w-4 h-4" />
                </Button>
                <div className="w-px h-6 bg-gray-300 mx-1" />
              </>
            )}

            {/* Print */}
            {(fileType === 'pdf' || fileType === 'image') && blobUrl && (
              <Button variant="ghost" size="sm" onClick={handlePrint} title={isRTL ? 'طباعة' : 'Print'}>
                <Printer className="w-4 h-4" />
              </Button>
            )}

            {/* Download */}
            {blobUrl && (
              <Button variant="ghost" size="sm" onClick={handleDownload} title={isRTL ? 'تحميل' : 'Download'}>
                <Download className="w-4 h-4" />
              </Button>
            )}

            {/* Fullscreen */}
            <Button variant="ghost" size="sm" onClick={toggleFullscreen} title={isRTL ? 'ملء الشاشة' : 'Fullscreen'}>
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>

            {/* Open in new tab */}
            {blobUrl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(blobUrl, '_blank')}
                title={isRTL ? 'فتح في نافذة جديدة' : 'Open in new tab'}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            )}

            {/* Close */}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden bg-gray-100">
          {renderContent()}
        </div>

        {/* Footer with document info */}
        {docData && (
          <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {docData.file_name && (
                <span>{isRTL ? 'اسم الملف:' : 'File:'} {docData.file_name}</span>
              )}
              {docData.uploaded_at && (
                <span>
                  {isRTL ? 'رفع في:' : 'Uploaded:'} {new Date(docData.uploaded_at).toLocaleDateString()}
                </span>
              )}
            </div>
            {fileType === 'image' && zoom !== 100 && (
              <Button variant="link" size="sm" className="text-xs h-auto p-0" onClick={handleResetZoom}>
                {isRTL ? 'إعادة ضبط' : 'Reset zoom'}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPreview;
